import { GameState } from "../models/game";
import { getApiBaseUrl } from "../config/environment";

/**
 * Extracts only the fields needed for the user-designs endpoint
 * from the full game state.
 */
function buildUserDesignPayload(gameState: GameState) {
  return {
    game: {
      nodes: gameState.nodes,
      edges: gameState.edges,
      blocks: gameState.blocks,
      timeElapsedInSec: gameState.timeElapsedInSec,
      trafficPattern: gameState.trafficPattern,
      trafficRoutes: gameState.trafficRoutes,
    },
  };
}

/**
 * Saves a snapshot of the current game state to the user-designs endpoint.
 * Fire-and-forget — errors are logged but do not block gameplay.
 */
export async function saveUserDesign(gameState: GameState): Promise<void> {
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const isGaDisabled = localStorage.getItem("admin_no_track") === "true" || 
                       window.location.search.includes("disable_ga=true") || 
                       window.location.search.includes("ga_disable=true");

  if (isLocalhost || isGaDisabled) {
    return;
  }

  try {
    const payload = buildUserDesignPayload(gameState);

    const response = await fetch(`${getApiBaseUrl()}/user-designs`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      console.error("Failed to save user design:", response.status, response.statusText);
    }
  } catch (err) {
    console.error("Error saving user design:", err);
  }
}

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------

export interface UserProfile {
  userId: string;
  email: string;
  name?: string;
  signUpMethod: "email" | "google";
  createdAt: string;
}

/** A non-2xx response from the API, carrying its status and `error` message. */
export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(response.status, data.error ?? "Something went wrong. Please try again.");
  }
  return data as T;
}

/** POST /users — create an account; Cognito emails a verification code. */
export function signUp(email: string, password: string, name?: string): Promise<{ userId: string; email: string }> {
  return postJson("/users", { email, password, ...(name ? { name } : {}) });
}

/** POST /users/confirm — verify the account with the emailed code. */
export function confirmSignUp(email: string, code: string): Promise<{ email: string; confirmed: boolean }> {
  return postJson("/users/confirm", { email, code });
}

/** POST /users/resend-code — email a fresh verification code. */
export function resendCode(email: string): Promise<{ codeDeliveryDestination?: string }> {
  return postJson("/users/resend-code", { email });
}

/**
 * GET /users/me — the signed-in user's profile. Returns null when the token is valid but the
 * user has no profile row yet (404). Throws ApiError for other failures (401 = bad token).
 */
export async function fetchCurrentUser(accessToken: string): Promise<UserProfile | null> {
  const response = await fetch(`${getApiBaseUrl()}/users/me`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 404) return null;
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(response.status, data.error ?? "Could not load your account.");
  }
  return data as UserProfile;
}
